/**
 * Cookie consent banner + preferences modal (Task #202 — GDPR).
 *
 * Behaviour summary:
 *  - First-visit banner pinned to the bottom of the viewport on every
 *    public and authed route. Cannot be dismissed implicitly — the
 *    visitor must click Accept all, Reject non-essential, or open the
 *    preferences modal and save a choice.
 *  - "Manage preferences" opens a focus-trapped, keyboard-accessible
 *    modal listing the three categories (Essential always-on, plus
 *    Analytics and Marketing). Each non-essential category expands to
 *    show vendor disclosures with a link to the vendor's privacy
 *    policy.
 *  - The same modal can be re-opened later via the persistent
 *    "Cookie settings" link in every footer (see Footer / SimpleFooter).
 */
import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  CONSENT_VERSION,
  useCookieConsent,
  type ConsentCategories,
} from "@/lib/cookie-consent";

interface VendorRow {
  name: string;
  purpose: string;
  privacyUrl: string;
}

interface Category {
  key: keyof ConsentCategories | "essential";
  label: string;
  description: string;
  vendors: VendorRow[];
  alwaysOn?: boolean;
}

const CATEGORIES: Category[] = [
  {
    key: "essential",
    label: "Essential",
    description:
      "Required for the site to function — keeping you signed in, remembering your session, protecting against CSRF attacks, and storing your cookie-consent choice itself. These cannot be turned off.",
    alwaysOn: true,
    vendors: [
      {
        name: "AI Shoot Lab",
        purpose: "Session, authentication, CSRF protection, and remembering your consent choice.",
        privacyUrl: "/privacy",
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Help us understand how visitors use the site so we can improve it. Aggregated only — no individual tracking, no advertising profiles.",
    vendors: [
      {
        name: "Plausible",
        purpose: "Privacy-focused, cookieless website analytics (page views, button clicks, scroll depth).",
        privacyUrl: "https://plausible.io/privacy",
      },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Used to measure the effectiveness of marketing campaigns when we run them. Empty today — no marketing vendors are loaded by AI Shoot Lab at this time.",
    vendors: [],
  },
];

export function CookieConsentBanner() {
  const {
    needsChoice,
    preferencesOpen,
    setPreferencesOpen,
    acceptAll,
    rejectNonEssential,
  } = useCookieConsent();

  if (!needsChoice && !preferencesOpen) return null;

  return (
    <>
      {needsChoice && !preferencesOpen ? (
        <BannerBar
          onAcceptAll={acceptAll}
          onReject={rejectNonEssential}
          onManage={() => setPreferencesOpen(true)}
        />
      ) : null}
      {preferencesOpen ? <PreferencesModal /> : null}
    </>
  );
}

// ─────────── Banner bar ───────────

function BannerBar({
  onAcceptAll,
  onReject,
  onManage,
}: {
  onAcceptAll: () => void;
  onReject: () => void;
  onManage: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[100] px-3 sm:px-4 pb-3 sm:pb-4 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className="pointer-events-auto max-w-5xl mx-auto bg-background border border-border shadow-2xl rounded-2xl p-4 sm:p-5">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="text-sm text-muted-foreground leading-relaxed lg:max-w-3xl">
            <p>
              <span className="font-semibold text-foreground">We use cookies.</span>{" "}
              Essential cookies keep you signed in and remember your preferences.
              With your consent we also use Analytics cookies to understand how the
              site is used. Read our{" "}
              <a href="/privacy" className="underline hover:text-foreground">
                Privacy Policy
              </a>{" "}
              for details.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 lg:shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onManage}
              className="sm:w-auto"
              data-testid="cookie-banner-manage"
            >
              Manage preferences
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReject}
              data-testid="cookie-banner-reject"
            >
              Reject non-essential
            </Button>
            <Button
              size="sm"
              onClick={onAcceptAll}
              data-testid="cookie-banner-accept"
            >
              Accept all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────── Preferences modal ───────────

function PreferencesModal() {
  const { state, setPreferencesOpen, acceptAll, rejectNonEssential, savePreferences } =
    useCookieConsent();
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Initialise toggle state from existing consent (if any).
  const [analytics, setAnalytics] = useState<boolean>(
    state?.categories.analytics ?? false,
  );
  const [marketing, setMarketing] = useState<boolean>(
    state?.categories.marketing ?? false,
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Lock body scroll, trap focus, capture/restore the previously
  // focused element, and close on Escape.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Move focus into the dialog on open.
    const t = window.setTimeout(() => {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.[0]?.focus();
    }, 0);

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setPreferencesOpen(false);
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [setPreferencesOpen]);

  function toggleExpand(key: string) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function handleSave() {
    savePreferences({ analytics, marketing });
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/60 px-3 sm:px-4"
      data-testid="cookie-preferences-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="w-full max-w-2xl bg-background border border-border rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        data-testid="cookie-preferences"
      >
        <header className="flex items-start justify-between gap-4 px-5 sm:px-6 py-4 border-b border-border">
          <div>
            <h2 id={titleId} className="text-lg font-display font-semibold tracking-tight">
              Cookie preferences
            </h2>
            <p id={descId} className="mt-1 text-sm text-muted-foreground">
              Choose which categories of cookies AI Shoot Lab can use. Essential cookies are always on.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPreferencesOpen(false)}
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-secondary text-muted-foreground"
            aria-label="Close cookie preferences"
            data-testid="cookie-preferences-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-3">
          {CATEGORIES.map((cat) => {
            const isOn =
              cat.alwaysOn || (cat.key === "analytics" ? analytics : cat.key === "marketing" ? marketing : false);
            const isExpanded = expanded[cat.key as string] ?? false;
            return (
              <section
                key={cat.key}
                className="border border-border rounded-xl"
                data-testid={`cookie-category-${cat.key}`}
              >
                <div className="flex items-start gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggleExpand(cat.key as string)}
                    aria-expanded={isExpanded}
                    aria-controls={`cookie-category-detail-${cat.key}`}
                    className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-secondary"
                    data-testid={`cookie-category-toggle-${cat.key}`}
                  >
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold">
                        {cat.label}
                        {cat.alwaysOn ? (
                          <span className="ml-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                            Always on
                          </span>
                        ) : null}
                      </h3>
                      <Switch
                        checked={isOn}
                        disabled={cat.alwaysOn}
                        onCheckedChange={(v) => {
                          if (cat.key === "analytics") setAnalytics(Boolean(v));
                          if (cat.key === "marketing") setMarketing(Boolean(v));
                        }}
                        aria-label={`${cat.label} cookies`}
                        data-testid={`cookie-toggle-${cat.key}`}
                      />
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                      {cat.description}
                    </p>
                  </div>
                </div>
                {isExpanded ? (
                  <div
                    id={`cookie-category-detail-${cat.key}`}
                    className="border-t border-border bg-secondary/40 px-4 py-3 text-sm space-y-2"
                  >
                    {cat.vendors.length === 0 ? (
                      <p className="text-muted-foreground">
                        No vendors are loaded for this category today.
                      </p>
                    ) : (
                      cat.vendors.map((v) => (
                        <div key={v.name} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                          <div className="font-medium min-w-32">{v.name}</div>
                          <div className="text-muted-foreground flex-1">
                            {v.purpose}{" "}
                            <a
                              href={v.privacyUrl}
                              target={v.privacyUrl.startsWith("http") ? "_blank" : undefined}
                              rel={v.privacyUrl.startsWith("http") ? "noreferrer" : undefined}
                              className="underline hover:text-foreground"
                            >
                              Privacy policy
                            </a>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : null}
              </section>
            );
          })}

          <p className="text-xs text-muted-foreground pt-2">
            Banner version v{CONSENT_VERSION}. We log each consent choice to a private
            audit table for compliance — no advertising data is shared.
          </p>
        </div>

        <footer className="flex flex-col sm:flex-row gap-2 sm:justify-end border-t border-border px-5 sm:px-6 py-4">
          <Button
            variant="ghost"
            onClick={rejectNonEssential}
            data-testid="cookie-preferences-reject"
          >
            Reject non-essential
          </Button>
          <Button
            variant="outline"
            onClick={acceptAll}
            data-testid="cookie-preferences-accept-all"
          >
            Accept all
          </Button>
          <Button onClick={handleSave} data-testid="cookie-preferences-save">
            Save preferences
          </Button>
        </footer>
      </div>
    </div>
  );
}

// ─────────── Persistent footer link ───────────

/**
 * Small inline button styled to match a footer link. Used in every
 * footer (landing Footer, SimpleFooter, in-app shell) so the visitor
 * can re-open the preferences modal at any time after the initial
 * choice.
 */
export function CookieSettingsLink({ className = "" }: { className?: string }) {
  const { setPreferencesOpen } = useCookieConsent();
  return (
    <button
      type="button"
      onClick={() => setPreferencesOpen(true)}
      className={`text-sm text-muted-foreground hover:text-foreground transition-colors ${className}`}
      data-testid="cookie-settings-link"
    >
      Cookie settings
    </button>
  );
}
